<template>
  <v-card
    class="expandable-filter-card pa-5"
    :elevation="elevation">
    <v-btn
      v-if="showExpandButton && hasExpandedContent"
      class="expandable-filter-card__expand-button elevation-1"
      :class="{
        'expandable-filter-card__expand-button--with-label': isHoveringExpandButton
          && expandButtonHoverLabel
      }"
      min-width="32"
      height="32"
      @mouseenter="isHoveringExpandButton = true"
      @mouseleave="isHoveringExpandButton = false"
      @click="toggleExpanded">
      <span
        v-if="isHoveringExpandButton && expandButtonHoverLabel"
        class="expandable-filter-card__expand-label">
        {{ expandButtonHoverLabel }}
      </span>
      <v-icon color="primary-accent" size="20">
        {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
      </v-icon>
    </v-btn>

    <slot
      name="always-visible"
      :is-expanded="isExpanded"
      :trigger-search="triggerSearch"
      :trigger-clear="triggerClear"
      :toggle-expanded="toggleExpanded"
    />

    <v-expand-transition
      @after-enter="emitExpandTransitionEnd"
      @after-leave="emitExpandTransitionEnd">
      <div v-show="isExpanded">
        <slot
          name="expanded"
          :is-expanded="isExpanded"
          :trigger-search="triggerSearch"
          :trigger-clear="triggerClear"
        />

        <div v-if="showActions" class="expandable-filter-card__actions">
          <template v-if="$scopedSlots.actions">
            <slot
              name="actions"
              :is-expanded="isExpanded"
              :trigger-search="triggerSearch"
              :trigger-clear="triggerClear"
            />
          </template>

          <div v-else class="expandable-filter-card__default-actions">
            <v-btn
              outlined
              color="muted-accent"
              class="expandable-filter-card__default-action"
              :disabled="clearDisabled || loading"
              @click="triggerClear">
              <v-icon left size="20">mdi-broom</v-icon>
              {{ clearLabel || $tc('common.clearFilter', 2) }}
            </v-btn>
            <v-btn
              elevation="0"
              color="primary"
              class="expandable-filter-card__default-action"
              :loading="loading"
              :disabled="searchDisabled"
              @click="triggerSearch">
              <v-icon left size="20">mdi-magnify</v-icon>
              {{ searchLabel || $tc('common.search', 2) }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
export default {
  name: 'ExpandableFilterCard',
  props: {
    startExpanded: {
      type: Boolean,
      default: false,
    },
    showExpandButton: {
      type: Boolean,
      default: true,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    elevation: {
      type: [Number, String],
      default: 1,
    },
    expandButtonHoverTextExpanded: {
      type: String,
      default: '',
    },
    expandButtonHoverTextCollapsed: {
      type: String,
      default: '',
    },
    autoSearchOnAlwaysVisibleChange: {
      type: Boolean,
      default: true,
    },
    alwaysVisibleValues: {
      type: [Object, Array, String, Number, Boolean],
      default: null,
    },
    searchAction: {
      type: Function,
      default: null,
    },
    clearAction: {
      type: Function,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    searchDisabled: {
      type: Boolean,
      default: false,
    },
    clearDisabled: {
      type: Boolean,
      default: false,
    },
    searchLabel: {
      type: String,
      default: '',
    },
    clearLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isExpanded: this.startExpanded,
      isHoveringExpandButton: false,
      isAlwaysVisibleWatcherReady: false,
      suppressAutoSearch: false,
    };
  },
  computed: {
    hasExpandedContent() {
      return Boolean(this.$scopedSlots.expanded || this.$slots.expanded);
    },
    expandButtonHoverLabel() {
      return this.isExpanded
        ? this.expandButtonHoverTextExpanded
        : this.expandButtonHoverTextCollapsed;
    },
  },
  watch: {
    isExpanded(newValue) {
      this.$emit('on-expand-change', newValue);
    },
    alwaysVisibleValues: {
      deep: true,
      handler() {
        if (!this.autoSearchOnAlwaysVisibleChange) return;
        if (!this.isAlwaysVisibleWatcherReady || this.suppressAutoSearch) return;
        this.triggerSearch();
      },
    },
  },
  mounted() {
    this.$emit('on-expand-change', this.isExpanded);
    this.isAlwaysVisibleWatcherReady = true;
  },
  methods: {
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },
    emitExpandTransitionEnd() {
      this.$emit('on-expand-transition-end', this.isExpanded);
    },
    triggerSearch() {
      if (typeof this.searchAction === 'function') {
        this.searchAction();
        return;
      }
      this.$emit('on-search');
    },
    triggerClear() {
      this.suppressAutoSearch = true;
      if (typeof this.clearAction === 'function') {
        this.clearAction();
      } else {
        this.$emit('on-clear');
      }
      this.$nextTick(() => {
        this.suppressAutoSearch = false;
      });
    },
  },
};
</script>

<style scoped>
.expandable-filter-card {
  border-color: #e0e0e0 !important;
  border-radius: 4px;
  overflow: visible !important;
  position: relative;
}

.expandable-filter-card__expand-button {
  align-items: center;
  background-color: white !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 16px !important;
  display: flex;
  padding: 0 6px !important;
  position: absolute;
  right: -16px;
  top: -16px;
  transition: padding 0.15s ease;
  z-index: 4;
}

.expandable-filter-card__expand-button--with-label {
  padding: 0 10px 0 6px !important;
}

.expandable-filter-card__expand-label {
  color: #5f6368;
  font-size: 12px;
  line-height: 1;
  margin-left: 6px;
  white-space: nowrap;
}

.expandable-filter-card__actions {
  margin-top: 28px;
}

.expandable-filter-card__default-actions {
  align-items: center;
  display: flex;
  gap: 20px;
  justify-content: flex-end;
}

.expandable-filter-card__default-action {
  border-radius: 6px !important;
  height: 50px !important;
  min-width: 165px !important;
}
</style>
